import { Injectable } from '@angular/core';
import { ROUTER_OUTLET_PREFIX, ROUTER_PATH_PREFIX, WebComponentRegistryItem, WebComponentRegistryPoolItem } from './types';

@Injectable({ providedIn: 'root' })
export class WebComponentRegistryService {
  public static readonly WC_BRIDGE_DATA_OUTLET_ATTR = 'data-outlet';
  public static readonly WC_BRIDGE_DATA_PATH_ATTR = 'data-path';
  public static readonly WC_BRIDGE_DATA_LOADING_ATTR = 'data-loading';
  public static readonly WC_BRIDGE_ROUTE_ATTR = 'route';
  private pool: Array<WebComponentRegistryPoolItem> = [];
  private contentLoaded = false;
  private _instance = 0;

  constructor() {
    window.addEventListener('load', () => {
      this.contentLoaded = true;
      this.run();
      /**
       * router-outlet has to wait until the DOM is fully ready, including the content loaded with the pjax. In case of
       * a page hard reload content is immediately loaded and we can start scheduling the navigation
       * (this.contentLoaded = true). In case of a pjax load we have to wait until the pjax content is fully loaded and
       * then start scheduling the navigation (this.contentLoaded = e.detail). PJAX_CONTENT_LOADED event is fired from
       * cplacePjaxContent.ts directive. This listener has to be registered after full window load because we have to be
       * sure the event is created before registering the listener.
       */
      document.addEventListener('PJAX_CONTENT_LOADED', (e: any) => {
        this.contentLoaded = e.detail;
        this.run();
      });
    });
  }

  public static addDataInstanceAttribute(element: HTMLElement, instance: WebComponentRegistryItem): void {
    element.setAttribute(WebComponentRegistryService.WC_BRIDGE_DATA_OUTLET_ATTR, instance.outletName);
    element.setAttribute(WebComponentRegistryService.WC_BRIDGE_DATA_LOADING_ATTR, 'true');
    element.setAttribute(WebComponentRegistryService.WC_BRIDGE_DATA_PATH_ATTR, instance.clientRootRoute);
  }

  private static getWcBridgeInstances(): string[] {
    const components = Array.from(document.querySelectorAll('cf-wc-bridge')) as HTMLElement[];
    return components.reduce((acc: string[], item: HTMLElement) => {
      const instance = item.getAttribute(WebComponentRegistryService.WC_BRIDGE_DATA_OUTLET_ATTR) as string;
      const route = item.getAttribute(WebComponentRegistryService.WC_BRIDGE_ROUTE_ATTR) as string;
      const loading = item.getAttribute(WebComponentRegistryService.WC_BRIDGE_DATA_LOADING_ATTR) as string;
      if (instance && route && loading) {
        acc.push(instance);
      }
      return acc;
    }, []);
  }

  private static removeDataLoadingAttr(outletName: string): void {
    const wc = document.querySelector(`cf-wc-bridge[${WebComponentRegistryService.WC_BRIDGE_DATA_OUTLET_ATTR}="${outletName}"]`);
    if (wc) {
      wc.removeAttribute(WebComponentRegistryService.WC_BRIDGE_DATA_LOADING_ATTR);
    }
  }

  public getInstance(): WebComponentRegistryItem {
    this._instance++;
    return {
      instance: this._instance,
      outletName: `${ROUTER_OUTLET_PREFIX}${this._instance}`,
      clientRootRoute: `${ROUTER_PATH_PREFIX}${this._instance}`,
    };
  }

  /**
   * When loading multiple MF apps on init - the routers must load sequentially
   * as they are all fighting for the same url / route.
   *
   * Normally: wc1, wc3 and wc3 load at the same time and both have the same url "/"
   * The last one to finished the navigation cycle wins -> i.e wc3 replaces "/" (same state / navigation id as all others)
   * with "route3"
   *
   * Desired outcome (sequential navigation each inherting the navigation state from the previous cycle):
   * wc1 => url = "" => "route1" (navigation cycle complete for wc1)
   * wc2 => url = "route1" => "route1//route2" (navigation cycle complete for wc2)
   * wc3 => url = "route1//route2" => "route1//route2//route3" (navigation cycle complete for wc3)
   */
  public add(registryPoolItem: WebComponentRegistryPoolItem): void {
    this.pool.push(registryPoolItem);
    this.run();
  }

  private run(): void {
    if (!this.contentLoaded) {
      return;
    }
    const wcBridgeInstances = WebComponentRegistryService.getWcBridgeInstances();
    if (wcBridgeInstances.length === this.pool.length) {
      this.schedule(this.pool.shift());
    }
  }

  private schedule(registryPoolItem: WebComponentRegistryPoolItem | undefined): void {
    if (!registryPoolItem) {
      return;
    }
    registryPoolItem.navigate().then(() => {
      WebComponentRegistryService.removeDataLoadingAttr(registryPoolItem.instance.outletName);
      this.schedule(this.pool.shift());
    });
  }
}
